import * as RadioGroup from '@radix-ui/react-radio-group';
import { AnimatePresence, motion } from 'framer-motion';
import { forwardRef, useState } from 'react';
import { SurveyRadioItem } from './survey-radio-item';
import { Input } from '~/components/ui/Input';

export interface Data {
  role?: Role;
  role_note?: string;
}

export type Role =
  | 'founder-entrepeneur'
  | 'pm'
  | 'designer'
  | 'developer'
  | 'marketer'
  | 'content-creator'
  | 'educator'
  | 'student'
  | 'other';

const ROLES: Array<{ key: Role; value: string }> = [
  { key: 'founder-entrepeneur', value: 'Founder / Entrepreneur' },
  { key: 'pm', value: 'Product Manager' },
  { key: 'designer', value: 'Designer' },
  { key: 'developer', value: 'Software Developer' },
  { key: 'marketer', value: 'Marketer' },
  { key: 'content-creator', value: 'Content Creator' },
  { key: 'educator', value: 'Educator' },
  { key: 'student', value: 'Student' },
  { key: 'other', value: 'Other' },
];

interface Props {
  initialValues?: Data;
  className?: string;
  validate: () => void;
}

const SurveyStep1 = forwardRef<HTMLDivElement, Props>(({ initialValues, className, validate }, ref) => {
  const [role, setRole] = useState(typeof initialValues?.role === 'string' ? initialValues?.role : '');

  return (
    <div className={className} ref={ref}>
      <h2 className="text-[32px]/[39px] font-semibold">What best describes your role?</h2>

      <RadioGroup.Root
        defaultValue={role}
        name="role"
        aria-label="Role"
        required
        className="mt-8 flex flex-wrap justify-center gap-3"
        onValueChange={setRole}
      >
        {ROLES.map((role) => (
          <SurveyRadioItem key={role.key} value={role.key} id={`role-${role.key}`}>
            {role.value}
          </SurveyRadioItem>
        ))}
      </RadioGroup.Root>

      <AnimatePresence onExitComplete={validate}>
        {role === 'Other' && (
          <motion.div
            className="flex items-center w-full gap-2 mt-4"
            initial={{ opacity: 0, x: -5 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -5 }}
            onAnimationComplete={validate}
          >
            <label htmlFor="role_note-input" className="text-bolt-ds-textSecondary text-sm font-medium">
              Please specify
            </label>
            <Input
              type="text"
              name="role_note"
              id="role_note"
              size="sm"
              placeholder="Describe your role..."
              autoFocus
              required
              className="flex-1"
              defaultValue={initialValues?.role_note}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
});

SurveyStep1.displayName = 'SurveyStep1';

export default SurveyStep1;
